import { Logger } from 'utilities';
import { INDEXED_DB_TIMEOUT } from './indexedDBStorageManager.constants';
import type {
  IndexedDBFallbackOperation,
  IndexedDBPendingFallback,
} from './indexedDBStorageManager.types';

type FallbackStorageKey = string | ((storageKey: string) => string);

type FallbackDeps = {
  memoryFallback: Map<string, IndexedDBPendingFallback>;
  fallbackStorageKey: FallbackStorageKey;
  logNamespace: string;
};

export const idbRequest = <T,>(req: IDBRequest<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export const idbTransaction = (transaction: IDBTransaction): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const activeTransaction = transaction;
    let timeoutId: ReturnType<typeof setTimeout>;
    const clearTransactionTimeout = () => clearTimeout(timeoutId);
    const rejectTransaction = (error: unknown) => {
      clearTransactionTimeout();
      reject(error);
    };

    activeTransaction.oncomplete = () => {
      clearTransactionTimeout();
      resolve();
    };
    activeTransaction.onerror = () => rejectTransaction(activeTransaction.error);
    activeTransaction.onabort = () => rejectTransaction(
      activeTransaction.error ?? new DOMException('IndexedDB transaction aborted', 'AbortError')
    );

    timeoutId = setTimeout(() => {
      try {
        activeTransaction.abort();
      } catch {
        // The transaction may already be inactive; the timeout still rejects the operation.
      }
      rejectTransaction(new Error('IndexedDB transaction timed out'));
    }, INDEXED_DB_TIMEOUT);
  });

export const getFallbackStorageKey = (key: string, fallbackStorageKey: FallbackStorageKey): string =>
  typeof fallbackStorageKey === 'function' ? fallbackStorageKey(key) : fallbackStorageKey;

export const attachVersionChangeHandler = (
  db: IDBDatabase,
  onConnectionClose: () => void,
): void => {
  // Close gracefully when another tab requests a version upgrade so that
  // tab's open request is not blocked indefinitely.
  const connection = db;
  connection.onversionchange = () => {
    connection.close();
    onConnectionClose();
  };
};

export const handleUpgradeNeeded = (event: IDBVersionChangeEvent, storeName: string): void => {
  const db = (event.target as IDBOpenDBRequest).result;
  if (!db.objectStoreNames.contains(storeName)) {
    db.createObjectStore(storeName, { keyPath: 'key' });
  }
};

export const handleOpenSuccess = (
  event: Event,
  resolve: (db: IDBDatabase) => void,
  clearOpenTimeout: () => void,
  onConnectionClose: () => void,
): void => {
  clearOpenTimeout();
  const db = (event.target as IDBOpenDBRequest).result;
  attachVersionChangeHandler(db, onConnectionClose);
  resolve(db);
};

export const handleOpenError = (
  error: DOMException | null,
  reject: (reason?: unknown) => void,
  clearOpenTimeout: () => void,
  onConnectionClose: () => void,
): void => {
  clearOpenTimeout();
  onConnectionClose();
  reject(error);
};

export const handleOpenBlocked = (logNamespace: string): void => {
  // A prior connection is holding an older DB version; the timeout will reject
  // if onsuccess does not fire once that connection closes.
  Logger.warn(`${logNamespace} IndexedDB open blocked by another connection`);
};

const isFallbackOperation = (value: unknown): value is IndexedDBFallbackOperation => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<IndexedDBFallbackOperation>;
  return (
    candidate.fallbackVersion === 1 &&
    (candidate.operation === 'remove' || candidate.operation === 'set')
  );
};

export const createSetFallbackOperation = <T,>(value: T): IndexedDBFallbackOperation<T> => ({
  fallbackVersion: 1,
  operation: 'set',
  value,
});

export const createRemoveFallbackOperation = (): IndexedDBFallbackOperation => ({
  fallbackVersion: 1,
  operation: 'remove',
});

export const readPendingFallback = <T,>({
  key,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & { key: string }): IndexedDBPendingFallback<T> | undefined => {
  if (memoryFallback.has(key)) {
    return memoryFallback.get(key) as IndexedDBPendingFallback<T>;
  }

  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem(getFallbackStorageKey(key, fallbackStorageKey));
    if (!raw) {
      return undefined;
    }

    const parsed = JSON.parse(raw) as unknown;
    const operation = isFallbackOperation(parsed)
      ? parsed as IndexedDBFallbackOperation<T>
      : createSetFallbackOperation(parsed as T);

    return {
      // Existing raw fallbacks are pending writes and remain recoverable after this upgrade.
      operation,
      serializedValue: raw,
    };
  } catch (error) {
    Logger.debug(`${logNamespace} fallback read failed:`, error);
    return undefined;
  }
};

export const writePendingFallback = ({
  key,
  operation,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & {
  key: string;
  operation: IndexedDBFallbackOperation;
}): IndexedDBPendingFallback => {
  const pendingFallback: IndexedDBPendingFallback = { operation };
  memoryFallback.set(key, pendingFallback);

  if (typeof window === 'undefined') {
    return pendingFallback;
  }

  const storageKey = getFallbackStorageKey(key, fallbackStorageKey);
  let existingSerializedValue: string | null = null;

  try {
    existingSerializedValue = window.localStorage.getItem(storageKey);
    const serializedValue = JSON.stringify(operation);
    window.localStorage.setItem(storageKey, serializedValue);
    pendingFallback.serializedValue = serializedValue;
  } catch (error) {
    pendingFallback.supersededSerializedValue = existingSerializedValue ?? undefined;
    Logger.debug(`${logNamespace} fallback write failed; using memory fallback:`, error);
  }

  return pendingFallback;
};

export const clearPendingFallback = ({
  key,
  pendingFallback,
  memoryFallback,
  fallbackStorageKey,
  logNamespace,
}: FallbackDeps & {
  key: string;
  pendingFallback: IndexedDBPendingFallback;
}): void => {
  if (memoryFallback.get(key) === pendingFallback) {
    memoryFallback.delete(key);
  }

  if (typeof window === 'undefined') {
    return;
  }

  try {
    const storageKey = getFallbackStorageKey(key, fallbackStorageKey);
    const currentSerializedValue = window.localStorage.getItem(storageKey);
    if (
      currentSerializedValue === pendingFallback.serializedValue ||
      currentSerializedValue === pendingFallback.supersededSerializedValue
    ) {
      window.localStorage.removeItem(storageKey);
    }
  } catch (error) {
    Logger.debug(`${logNamespace} fallback removal failed:`, error);
  }
};
